import { BuildQueryParamLinkQueryParams } from 'lib/helpers/urlHelper';
import { createContext, useContext } from 'react';
import { DEFAULT_METADATA } from '../constants';
import {
  ILearningCenterMetadata,
  LearningCenterMode,
  LearningCenterUserSettingKey,
  LearningCenterUserSettingValue,
} from '../types';

interface ILearningCenterContext
  extends Omit<ILearningCenterMetadata, 'organization'> {
  /**
   * Build a URL from the specified `path`, preserving the `key` URL param.
   *
   * This should **always** be used to create URLs that are used within the
   * Learning Center, since it ensures that a `softLoggedIn` user will continue
   * to be so.
   */
  buildLearningCenterUrl: (
    path: string,
    queryParams?: BuildQueryParamLinkQueryParams
  ) => string;

  /** Whether the user is authenticated. */
  isAuthenticated: boolean;

  isClassroomMode: boolean;
  isClassroomPreviewMode: boolean;
  isCmsMode: boolean;
  isLearningLibraryMode: boolean;
  isPreviewMode: boolean;
  isTrainingMode: boolean;

  /** The key for kiosk mode. We pass this with some requests and is
   * used to know if we are in kiosk mode. This is not an auth token.
   */
  kioskKey?: string;

  /** Logout url for a learner */
  logoutUrl: string;

  /** The render mode (preview, training, etc.). */
  mode: LearningCenterMode;

  /**
   * Since SCORM is loaded inside of an iframe, we can't use cookies like we could
   * if the learning center was loaded normally. This is the userKey when in scorm mode.
   * We need to pass around the scormKey to set the X-Scorm-Key header to authenticate
   * requests.
   */
  scormKey?: string;

  /**
   * Update a user setting locally and if associated with a session, remotely
   * as well.
   */
  updateUserSetting: (
    key: LearningCenterUserSettingKey,
    value: LearningCenterUserSettingValue
  ) => Promise<void>;

  /**
   * The token for the companies using sso
   */
  ssoConnection?: string;
}

export const LearningCenterContext = createContext<ILearningCenterContext>({
  ...DEFAULT_METADATA,
  buildLearningCenterUrl: () => '',
  isAuthenticated: false,
  isClassroomMode: false,
  isClassroomPreviewMode: false,
  isCmsMode: false,
  isLearningLibraryMode: false,
  isPreviewMode: false,
  isTrainingMode: true,
  kioskKey: undefined,
  logoutUrl: undefined,
  mode: undefined,
  scormKey: undefined,
  ssoConnection: undefined,
  updateUserSetting: async () => {},
});

export const useLearningCenterContext = () => useContext(LearningCenterContext);
